import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { BillingTypeModel, BillingTypeRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['billingTypes']

type BillingTypeFilters = {
  charges_type?: string,
}

type UseBillingTypesOptions = {
  filters?: BillingTypeFilters,
  performHttpRequests?: boolean,
  requestOptions?: BillingTypeRequestOptions,
}

function useBillingTypes(options: UseBillingTypesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    charges_type: filterString,
  } = filters

  const {
    updatedEntities: { billingTypes: billingTypesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { billingTypes } = useSelector(reduxState => reduxState.entities)

  const filteredBillingTypes = useMemo(() => {
    const filtered = Object.values(billingTypes).filter((billingType: BillingTypeModel) => {
      const { charges_type } = billingType

      const matchChargesType = matchFilterString(charges_type, filterString, { exact: true })

      return matchChargesType
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [billingTypesUpdatedAt, JSON.stringify(filters)])

  const filteredBillingTypesCount = filteredBillingTypes.length
  const hasFilteredBillingTypes = !!filteredBillingTypesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBillingTypesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBillingTypes } = useReduxAction(
    'billingTypes',
    'loadBillingTypes',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBillingTypes,
    filteredBillingTypesCount,
    hasFilteredBillingTypes,
    loading: loadingBillingTypes,
  }
}

export default useBillingTypes
